import type { Point } from "../inp/types.ts";

export function buildTangentPlaneCrs(lon0: number, lat0: number): string {
  return `+proj=tmerc +lat_0=${lat0} +lon_0=${lon0} +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs`;
}

export interface ControlPoint {
  local: Point;
  target: Point;
}

export interface SimilarityFit {
  apply: (p: Point) => Point;
  scale: number;
  rotationDeg: number;
  /** A large residual (vs. its neighbors) flags a likely mis-click. */
  residuals: number[];
}

/** `null` for fewer than 2 points, or a degenerate one where every local point coincides (nothing to derive a scale/rotation from). */
export function fitSimilarityTransform(points: ControlPoint[]): SimilarityFit | null {
  if (points.length < 2) return null;

  const n = points.length;
  const localCentroid = {
    x: points.reduce((sum, p) => sum + p.local.x, 0) / n,
    y: points.reduce((sum, p) => sum + p.local.y, 0) / n,
  };
  const targetCentroid = {
    x: points.reduce((sum, p) => sum + p.target.x, 0) / n,
    y: points.reduce((sum, p) => sum + p.target.y, 0) / n,
  };

  // z_i = local point, centered (complex: re=x, im=y); w_i = target point, centered.
  let sumConjZW = { re: 0, im: 0 }; // Σ conj(z_i) * w_i
  let sumAbsZ2 = 0; // Σ |z_i|²
  for (const p of points) {
    const z = { re: p.local.x - localCentroid.x, im: p.local.y - localCentroid.y };
    const w = { re: p.target.x - targetCentroid.x, im: p.target.y - targetCentroid.y };
    // conj(z) * w = (z.re - i*z.im) * (w.re + i*w.im)
    sumConjZW = {
      re: sumConjZW.re + (z.re * w.re + z.im * w.im),
      im: sumConjZW.im + (z.re * w.im - z.im * w.re),
    };
    sumAbsZ2 += z.re * z.re + z.im * z.im;
  }
  if (sumAbsZ2 < 1e-9) return null;

  const a = { re: sumConjZW.re / sumAbsZ2, im: sumConjZW.im / sumAbsZ2 };

  function apply(p: Point): Point {
    const z = { re: p.x - localCentroid.x, im: p.y - localCentroid.y };
    // a * z (complex multiplication), then re-add the target centroid.
    return {
      x: a.re * z.re - a.im * z.im + targetCentroid.x,
      y: a.re * z.im + a.im * z.re + targetCentroid.y,
    };
  }

  const residuals = points.map((p) => {
    const predicted = apply(p.local);
    return Math.hypot(predicted.x - p.target.x, predicted.y - p.target.y);
  });

  return {
    apply,
    scale: Math.hypot(a.re, a.im),
    rotationDeg: (Math.atan2(a.im, a.re) * 180) / Math.PI,
    residuals,
  };
}
